import { useState } from 'react';
import type { ApiClient, ExcelVorschau } from '../../api';
import { IconHochladen } from '../../Icons';
import { DataTable, Drawer, ErrorState, ExportLeiste, KpiCard, KpiReihe, formatiereZahl } from '../../muster';
import { tabelle, type Traeger } from './bestandregeln';

/**
 * ═══════════════════════════════════════════════════════════════════════════
 * DIE TABELLE DES LÜFTUNGSBESTANDS: AUSGEBEN UND EINLESEN (TV6c)
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * Ausgabe über die `ExportLeiste` (Excel), Einlesen in zwei Schritten - erst
 * zeigen, was sich ändern würde, dann übernehmen. Die Datei war zwischen-
 * zeitlich in fremden Händen: Jemand hat vielleicht Zeilen sortiert, eine
 * Spalte verschoben oder in ein Zahlenfeld „ca. 1800“ geschrieben.
 *
 * Die Aufrufe sind die alten (`lueftungExcel` mit Dateiname und Umfang aus
 * `bestandregeln.tabelle`, `lueftungEinlesen` mit `false`, dann `true`). Neu:
 * Die Vorschau steht im Schubfach und ohne Kappung (vorher 8 nicht
 * zuordenbare und 6 Auffälligkeiten, danach „…“) - der Server begrenzt auf
 * 300 Änderungen und je 100 Meldungen, das steht dabei.
 */
export function Tabellenaktionen({ api, projektId, umfang, neuLaden }: {
  api: ApiClient;
  projektId: string;
  umfang: Traeger | null;
  neuLaden: () => void;
}) {
  const t = tabelle(umfang);
  return (
    <>
      <ExportLeiste ziele={[{
        art: 'excel',
        beschriftung: t.beschriftung,
        hinweis: umfang ? `nur ${umfang.name}` : 'Gesamtbestand - links eine Anlage wählen für einen kleineren Ausschnitt',
        laden: () => api.lueftungExcel(projektId, t.dateiname, t.umfang),
      }]}
      />
      <Einlesen api={api} projektId={projektId} neuLaden={neuLaden} />
    </>
  );
}

/** Eine Zeile der Vorschau mit ihrer laufenden Nummer (Änderungen tragen keine eigene Kennung). */
type Meldezeile = { zeile: string };
type Textzeile = { text: string };
type Aenderungszeile = ExcelVorschau['aenderungen'][number] & { nr: number };

/** Die Grenzen, die der Server der Vorschau setzt (`LueftungExcel.LesenAsync`). */
const SERVERGRENZE = { aenderungen: 300, meldungen: 100 };

function Einlesen({ api, projektId, neuLaden }: { api: ApiClient; projektId: string; neuLaden: () => void }) {
  const [datei, setDatei] = useState<File | null>(null);
  const [vorschau, setVorschau] = useState<ExcelVorschau | null>(null);
  const [laeuft, setLaeuft] = useState(false);
  const [fehler, setFehler] = useState<string | null>(null);
  const [fertig, setFertig] = useState<string | null>(null);

  const pruefen = async (gewaehlt: File) => {
    setDatei(gewaehlt);
    setVorschau(null);
    setFehler(null);
    setFertig(null);
    setLaeuft(true);
    try {
      setVorschau((await api.lueftungEinlesen(projektId, gewaehlt, false)).vorschau);
    } catch (e) {
      setFehler(e instanceof Error ? e.message : String(e));
    } finally {
      setLaeuft(false);
    }
  };

  const uebernehmen = async () => {
    if (!datei) return;
    setLaeuft(true);
    setFehler(null);
    try {
      const ergebnis = await api.lueftungEinlesen(projektId, datei, true);
      setFertig(`${ergebnis.vorschau.aenderungen.length} Änderung(en) übernommen.`);
      setVorschau(null);
      setDatei(null);
      neuLaden();
    } catch (e) {
      setFehler(e instanceof Error ? e.message : String(e));
    } finally {
      setLaeuft(false);
    }
  };

  const schliessen = () => { setVorschau(null); setDatei(null); };

  return (
    <>
      <label className="m-knopf lue-dateiknopf">
        <IconHochladen size={16} />
        {laeuft && !vorschau ? 'wird geprüft …' : 'Tabelle einlesen'}
        <input
          type="file"
          accept=".xlsx"
          className="lue-verborgen"
          disabled={laeuft}
          onChange={(e) => {
            const gewaehlt = e.target.files?.[0];
            e.target.value = '';
            if (gewaehlt) void pruefen(gewaehlt);
          }}
        />
      </label>

      <span className="lue-meldung" role="status">{fertig ?? ''}</span>
      {fehler && !vorschau && <ErrorState klein meldung="Die Tabelle konnte nicht eingelesen werden." technisch={fehler} />}

      {vorschau && (
        <Drawer
          offen
          titel="Was sich ändern würde"
          beschreibung={`${datei?.name ?? ''} · erst prüfen, dann übernehmen`}
          breite="720px"
          beiSchliessen={schliessen}
          fuss={(
            <>
              <button type="button" className="m-knopf" disabled={laeuft} onClick={schliessen}>Abbrechen</button>
              <button
                type="button"
                className="m-knopf m-knopf-haupt"
                disabled={laeuft || vorschau.aenderungen.length === 0}
                onClick={() => void uebernehmen()}
              >
                {laeuft ? 'wird übernommen …' : `${vorschau.aenderungen.length} Änderung(en) übernehmen`}
              </button>
            </>
          )}
        >
          <KpiReihe>
            <KpiCard titel="Zeilen gelesen" wert={vorschau.zeilenGelesen} />
            <KpiCard titel="zugeordnet" wert={vorschau.zugeordnet} />
            <KpiCard titel="unverändert" wert={vorschau.unveraendert} />
            <KpiCard titel="Änderungen" wert={vorschau.aenderungen.length} />
          </KpiReihe>

          {fehler && <ErrorState klein meldung="Die Änderungen konnten nicht übernommen werden." technisch={fehler} />}

          {vorschau.nichtZuordenbar.length > 0 && (
            <DataTable<Meldezeile>
              name="Nicht zuordenbar"
              beschreibung="Ohne die verborgene erste Spalte (Elementkennung) findet das Einlesen die Zeile nicht wieder."
              trefferText={vorschau.nichtZuordenbar.length >= SERVERGRENZE.meldungen ? `die ersten ${SERVERGRENZE.meldungen} - der Server meldet nicht mehr` : undefined}
              spalten={[{ schluessel: 'zeile', titel: 'Zeile', wert: (z) => z.zeile }]}
              zeilen={vorschau.nichtZuordenbar.map((zeile) => ({ zeile }))}
              schluessel={(z) => z.zeile}
              dicht
            />
          )}
          {vorschau.auffaelligkeiten.length > 0 && (
            <DataTable<Textzeile>
              name="Auffälligkeiten"
              beschreibung="Werte, die nicht übernommen werden (z. B. Text in einem Zahlenfeld)."
              trefferText={vorschau.auffaelligkeiten.length >= SERVERGRENZE.meldungen ? `die ersten ${SERVERGRENZE.meldungen} - der Server meldet nicht mehr` : undefined}
              spalten={[{ schluessel: 'text', titel: 'Meldung', wert: (z) => z.text }]}
              zeilen={vorschau.auffaelligkeiten.map((text) => ({ text }))}
              schluessel={(z) => z.text}
              dicht
            />
          )}
          <DataTable<Aenderungszeile>
            name="Änderungen"
            trefferText={vorschau.aenderungen.length >= SERVERGRENZE.aenderungen
              ? `die ersten ${formatiereZahl(SERVERGRENZE.aenderungen, 0)} - übernommen werden alle`
              : undefined}
            spalten={[
              { schluessel: 'nummer', titel: 'Bauteil', wert: (a) => a.nummer, immerSichtbar: true },
              { schluessel: 'feld', titel: 'Feld', wert: (a) => a.feld },
              { schluessel: 'alt', titel: 'bisher', wert: (a) => a.alt ?? '—' },
              { schluessel: 'neu', titel: 'neu', wert: (a) => a.neu ?? 'gelöscht', inhalt: (a) => <strong>{a.neu ?? 'gelöscht'}</strong> },
            ]}
            zeilen={vorschau.aenderungen.map((a, nr) => ({ ...a, nr }))}
            schluessel={(a) => String(a.nr)}
            dicht
            leer={<span>Keine Änderung - die Tabelle entspricht dem Bestand.</span>}
          />
        </Drawer>
      )}
    </>
  );
}
